import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { BookOpen, Users, Church, Heart, GraduationCap, Baby, HandHeart, MessageCircle, Mail } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const Ministries = () => {
  const { language } = useLanguage();

  const ministries = [
    {
      icon: BookOpen,
      title: language === 'en' ? 'Sunday School' : 'ትምህርቲ ስንበት',
      description: language === 'en' 
        ? 'Biblical education for all ages - teaching children and youth the foundations of Orthodox faith in English, and Tigrinya.'
        : 'ንኹሎም ዕድመታት - ን ቆልዑትን መንእሰያትን መሰረታዊ እምነት ኦርቶዶክስን ትምህርቲ ስንበትን ብእንግሊዝኛ፡ ትግርኛን ምምሃር።',
      activities: language === 'en' 
        ? ['Weekly Sunday lessons', 'Age-appropriate classes', 'Bible study and memorization', 'Hymn learning']
        : ['ሰሙናዊ ትምህርቲ ስንበት', 'ንህጻናት በብዕድሚኦም ምምሃር', 'መጽሓፍ ቅዱስን ጥቅስታት ምምሃር', 'ትምህርቲ ቅዳሴ'],
      color: 'from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      icon: Users,
      title: language === 'en' ? 'Youth Fellowship' : 'መድብ መንእሰያት',
      description: language === 'en'
        ? 'Building strong spiritual bonds among young people through fellowship, service, and faith formation programs.'
        : 'ኣብ መንጎ መንእሰያት ድልዱል መንፈሳዊ ፍቅርንር ሕውነትን ምፍጣር፡ ብሓባር ምዕያይን ምግልጋልን።',
      activities: language === 'en'
        ? ['Weekly program', 'Sports and social activities', 'Community service projects', 'Leadership development']
        : ['ሰሙናዊ መድብ', 'ስፖርትን ማሕበራውን ንጥፈታት', 'ናይ ማሕበረሰብ ኣገልግሎትን ፕሮጀክትታትን', 'ትምህርቲ ምሕደራን መሪሕነት'],
      color: 'from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-amber-600 dark:text-amber-400'
    },
    {
      icon: Church,
      title: language === 'en' ? 'Deacons Ministry' : 'ኣገልግሎት ዲያቆናት',
      description: language === 'en'
        ? 'Training in liturgical service, hymns, and church tradition. Deacons serve during Divine Liturgy and lead in prayer.'
        : 'ስልጠና ትምህርቲ ቅዳሴን፡ ዜማን፡ መዝሙራትን። ዲያቆናት ኣብ ኣገልግሎት ቤት-ክርስትያን ቁድስ ቁርባን ብንጥፈት  ምምራሓ።',
      activities: language === 'en'
        ? ['Liturgical training', 'Hymn and response learning', 'Bible studies', 'Service during Divine Liturgy']
        : ['ስልጠና ትምህርቲ ቅዳሴ', 'መዝሙርን ዜማን ምምሃር', 'ትምህርቲ መጽሓፍ ቅዱስ', 'ኣብ ቅዱስ ቁርባን ኣገልግሎት'],
      color: 'from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-purple-600 dark:text-purple-400'
    },
    {
      icon: Heart,
      title: language === 'en' ? "Women's Ministry" : 'መድብ ደቂ-ኣንስትዮ',
      description: language === 'en'
        ? 'Supporting women through prayer meetings, Bible studies, and fellowship. Strengthening faith and building community.'
        : 'ን ደቂ-ኣንስትዮ ብጸሎትን፡ ትምህርት መጽሓፍ ቅዱስ ምድጋፍ። ምሕያል እምነትን ምህናጽ ማሕበረሰብን።',
      activities: language === 'en'
        ? ['Monthly prayer meetings', 'Bible study groups', 'Service and outreach', 'Fellowship']
        : ['ወርሓዊ ኣኼባ ጸሎት', 'ጉጅለታት ትምህርቲ መጽሓፍ ቅዱስ', 'ኣገልግሎትን ምብጻሕን', 'ሕበርት'],
      color: 'from-rose-50 to-red-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-rose-600 dark:text-rose-400'
    },
    {
      icon: GraduationCap,
      title: language === 'en' ? 'Adult Education' : 'ትምህርቲ ዓበይቲ',
      description: language === 'en'
        ? 'Bible study, theology, and spiritual lessons for adults. Deepening understanding of Orthodox faith and practice.'
        : 'ትምህርቲ መጽሓፍ ቅዱስን መንፈሳዊ ትምህርቲ ንዓበይቲ። ዕምቆት ዘለዎ እምነት ኦርቶዶክስ ምርዳኣ።',
      activities: language === 'en'
        ? ['Weekly Bible study', 'Theological discussions', 'Church history and tradition', 'Spiritual retreats']
        : ['ሰሙናዊ ትምህርቲ መጽሓፍ ቅዱስ', 'መለኮታዊ ትምህርቲ', 'ታሪኽን ባህልን ቤተ ክርስትያን', 'መንፈሳዊ መድባት'],
      color: 'from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-green-600 dark:text-green-400'
    },
    {
      icon: Baby,
      title: language === 'en' ? "Children's Ministry" : 'ኣገልግሎት ህጻናት',
      description: language === 'en'
        ? 'Creating a nurturing environment where children can learn about God, grow in faith, and build lasting friendships.'
        : 'ህጻናት፡ ብዛዕባ እግዚኣብሄር ክመሃሩ፡ ኣብ እምነት ክዓብዩን፡ ነባሪ ምሕዝነት ክሃንጹን ዝኽእልሉ ኣከባቢ ምፍጣር።',
      activities: language === 'en'
        ? ['Age-appropriate lessons', 'Crafts and activities', 'Biblical storytelling', 'Play and fellowship']
        : ['ን ህጻናት በብዕድሚኦም ምምሃር', 'ስራሕቲ ኢድን ካልእ ንጥፈታትን ምክያድ', 'ዛንታ መጽሓፍ ቅዱስ', 'ጸወታን ሕበርነትን'],
      color: 'from-yellow-50 to-amber-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-yellow-600 dark:text-yellow-500'
    },
    {
      icon: HandHeart,
      title: language === 'en' ? 'Social Services' : 'ማሕበራዊ ኣገልግሎት',
      description: language === 'en'
        ? 'Serving the community through charitable works, visiting the sick, supporting families in need, and outreach programs.'
        : 'ን ማሕበረሰብና ምድጋፍ፡ ንሕሙማት ምብጻሕ፡ ንስድራቤታት ከከም ዘድልዮም ምድጋፍን ምብጻሕን።',
      activities: language === 'en'
        ? ['Hospital and home visits', 'Food and clothing drives', 'Support for new immigrants', 'Community partnerships']
        : ['ንሕሙማት ኣብ ሆስፒታልን ገዛን ምብጻሕ', 'መግብን ክዳውንትን ምእካብ', 'ንሓደስቲ ስደተኛታት ምድጋፍ', 'ን ማሕበረሰብና ምድጋፍን ምትሕብባር'],
      color: 'from-teal-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700',
      iconColor: 'text-teal-600 dark:text-teal-400'
    }
  ];

  return (
    <>
      <PageMeta 
        title="Ministries"
        path="/ministries"
        description="Explore our church ministries including Sunday School, Youth Fellowship, Deacons Ministry, Women's Ministry, Adult Education, Children's Ministry, and Social Services."
      />
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {language === 'en' ? 'Our Ministries' : 'ኣገልግሎትና'}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {language === 'en' 
                ? 'Serving our community through diverse programs that nurture faith, build relationships, and spread the love of Christ'
                : 'ንማሕበረሰብና ብዝተፈላለዩ መደባት ብምስርዓ እምነቶም ዘዓቢሉ፡ ፍቕሪ ክርስቶስ ኣብ ልቦም ዝሰርተልን ዘነቃቕሑሉን ኣገልግሎት'
              }
            </p>
          </div>
        </section>

        {/* Ministries Grid */}
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {ministries.map((ministry, index) => {
                const IconComponent = ministry.icon;
                return (
                  <div key={index} className={`bg-gradient-to-br ${ministry.color} p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow`}>
                    <div className="flex items-center mb-4">
                      <IconComponent className={`h-10 w-10 ${ministry.iconColor} mr-4`} />
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {ministry.title}
                      </h3>
                    </div>
                    <p className="text-gray-700 dark:text-gray-200 mb-6 leading-relaxed">
                      {ministry.description}
                    </p>
                    
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                        {language === 'en' ? 'Activities:' : 'ንጥፈታት:'}
                      </h4>
                      <ul className="space-y-2">
                        {ministry.activities.map((activity, actIndex) => (
                          <li key={actIndex} className="flex items-start">
                            <span className="text-amber-600 dark:text-amber-400 mr-2 mt-1">•</span>
                            <span className="text-gray-600 dark:text-gray-300">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stay Connected Section */}
        <section className="py-16 bg-gradient-to-r from-amber-500 to-orange-500 dark:from-amber-700 dark:to-orange-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <MessageCircle className="h-16 w-16 text-white mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'en' ? 'Stay Connected' : 'ርከብና'}
            </h2>
            <p className="text-xl text-white mb-6">
              {language === 'en'
                ? 'Join our community on social media and WhatsApp for updates about services, events, and prayer requests.'
                : 'ንሓበሬታት ኣገልግሎት፡ ሕቶታት ጸሎትን ሕብረትን፡ ኣብ ማሕበራዊ መራኸቢታትን WhatsApp ን ተጸምበሩና።'
              }
            </p>
            <p className="text-white text-lg mb-6">
              {language === 'en'
                ? 'Click the WhatsApp icon to join our community group'
                : 'ንናይ ሕብረትና WhatsApp ጉጅለ ንምጽንባር ኣብ ታሕቲ ዘሎ ናይ WhatsApp ጥብቆ ጠውቁ'
              }
            </p>
          </div>
        </section>

        {/* Get Involved Section */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              {language === 'en' ? 'Get Involved' : 'ዑድማት ኢኩም'}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {language === 'en'
                ? "Everyone is welcome to participate in our ministries. Whether you want to serve, learn, or fellowship, there's a place for you."
                : 'ኣብ ኣገልግሎታትና ንክትሳተፉ ዑድማት ኢኩም። ክትገልግሉ፡ ክትመሃሩ ወይ ክትሓብሩ እንተደልኹም እንቃዓ ብድሓን መጻኩም።'
              }
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105"
            >
              <Mail className="w-5 h-5 mr-2" />
              {language === 'en' ? 'Contact Us to Get Started' : 'ንምጅማር ርኸቡና'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Ministries;